/**
 * libtebhoscore WASM yükleyicisi — kaynak-referans `features/platform/utils/LibFluxcore.ts`
 * 1-1 portunun zstd-stream alt-kümesi (kapsam kararı: rust/libtebhoscore/Cargo.toml
 * başlığına bkz — görsel/crop katmanı bu turda port edilmedi).
 *
 * Base64 gömülü `libtebhoscore_bg.wasm`'ı (LibTebhosCoreWasmBytes.ts) senkron
 * `initSync({module})` ile instantiate eder — kaynak-referansın ayrı .wasm asset +
 * bundler-URL fetch akışı YERİNE TEBHOS'un markdown-parser deseni (commit'li
 * base64, ağ isteği yok, worker/main-thread farkı yok).
 */

import type { InitOutput } from "./wasm/LibTebhosCoreWasmBindgen";
import * as wasm from "./wasm/LibTebhosCoreWasmBindgen";
import { LIBTEBHOSCORE_WASM_BASE64 } from "./wasm/LibTebhosCoreWasmBytes";

const MAX_RETAINED_WASM_MEMORY_BYTES = 64 * 1024 * 1024;

let moduleReady = false;
let moduleExports: InitOutput | null = null;
let activeZstdStreamHandles = 0;

function decodeBase64(value: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(value, "base64"));
  }
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function loadModule(): void {
  if (moduleReady) return;
  const bytes = decodeBase64(LIBTEBHOSCORE_WASM_BASE64);
  moduleExports = wasm.initSync({ module: bytes });
  moduleReady = true;
}

/** libtebhoscore WASM modülü yüklü mü (senkron kontrol). */
export function isLibTebhosCoreReady(): boolean {
  return moduleReady;
}

/** libtebhoscore WASM modülünün yüklendiğinden emin ol (senkron — ağ isteği yok). */
export async function ensureLibTebhosCoreReady(): Promise<void> {
  loadModule();
}

/**
 * WASM linear memory belirtilen eşiği (64MiB) aşmışsa VE aktif stream handle
 * yoksa modülü serbest bırakır (kaynak-referans releaseLibfluxcoreMemoryIfIdle 1-1).
 * Sonraki çağrı sıfırdan instantiate eder.
 */
export function releaseLibTebhosCoreMemoryIfIdle(): void {
  if (activeZstdStreamHandles > 0) return;
  if (!moduleReady) return;
  if ((moduleExports?.memory.buffer.byteLength ?? 0) <= MAX_RETAINED_WASM_MEMORY_BYTES) return;
  moduleReady = false;
  moduleExports = null;
}

function toUint8Array(value: Uint8Array | ArrayLike<number>): Uint8Array {
  return value instanceof Uint8Array ? value : new Uint8Array(value);
}

/** Tek-seferlik (stateless) zstd frame decompress — bkz. rust/libtebhoscore lib.rs başlığı. */
export async function decompressZstdFrame(input: Uint8Array): Promise<Uint8Array> {
  loadModule();
  try {
    return toUint8Array(wasm.decompress_zstd_frame(input));
  } finally {
    releaseLibTebhosCoreMemoryIfIdle();
  }
}

/** Yeni bir streaming zstd decoder (DCtx) oluşturur — bağlantı ömrü boyunca TEK örnek kullanılmalı. */
export async function createZstdStreamDecoder(): Promise<number> {
  loadModule();
  const decoder = wasm.create_zstd_stream_decoder();
  activeZstdStreamHandles++;
  return decoder;
}

/** Bir WS binary frame'ini (zstd stream flush parçası) mevcut decoder state'ine besler. */
export async function decompressZstdStreamChunk(decoder: number, input: Uint8Array): Promise<Uint8Array> {
  loadModule();
  return toUint8Array(wasm.decompress_zstd_stream_chunk(decoder, input));
}

export function freeZstdStreamDecoder(decoder: number): void {
  try {
    wasm.free_zstd_stream_decoder(decoder);
  } finally {
    activeZstdStreamHandles = Math.max(0, activeZstdStreamHandles - 1);
    releaseLibTebhosCoreMemoryIfIdle();
  }
}

export async function createZstdStreamEncoder(level: number): Promise<number> {
  loadModule();
  const encoder = wasm.create_zstd_stream_encoder(level);
  activeZstdStreamHandles++;
  return encoder;
}

/** Senkron varyant — modül zaten hazırsa (warmup sonrası) await'siz encoder açar (kaynak-referans createZstdStreamEncoderSync 1-1). */
export function createZstdStreamEncoderSync(level: number): number {
  loadModule();
  const encoder = wasm.create_zstd_stream_encoder(level);
  activeZstdStreamHandles++;
  return encoder;
}

export function compressZstdStreamChunk(encoder: number, input: Uint8Array): Uint8Array {
  return toUint8Array(wasm.compress_zstd_stream_chunk(encoder, input));
}

export function freeZstdStreamEncoder(encoder: number): void {
  try {
    wasm.free_zstd_stream_encoder(encoder);
  } finally {
    activeZstdStreamHandles = Math.max(0, activeZstdStreamHandles - 1);
    releaseLibTebhosCoreMemoryIfIdle();
  }
}
